<template>
  <div
    :class="[`dropdown-${appearance}`, {'dropdown-disabled': disabled }]"
    class="dropdown-container width-100"
  >
    <input
      ref="fakeInput"
      class="dropdown-hidden-input"
      type="text"
      @focus="dropdownFocus"
    >
    <!--
      Port Vue 3: bp-vuejs-dropdown (Vue 2, rimosso) → floating-vue VDropdown.
      Slot di default = trigger (btn); slot #popper = body. `close-on-click`
      ottenuto chiamando `hide()` (esposto dallo slot #popper) alla selezione.
    -->
    <VDropdown
      v-model:shown="shown"
      :triggers="['click']"
      :distance="4"
      :disabled="disabled"
      placement="bottom-start"
      popper-class="dropdown-popper"
    >
      <div
        class="dropdown-button-container width-100 is-flex"
      >
        <div
          v-if="icon"
          class="dropdown-icon is-flex align-center"
        >
          <i
            :class="[`icon-${icon}`]"
            class="icon"
          />
        </div>
        <div
          :class="{'dropdown-button--filled': !!selected}"
          class="dropdown-button width-100"
        >
          <a
            :style="dropdownValueStyle"
            :class="{'dropdown-value--capitalize': capitalize}"
            class="dropdown-value width-100"
          >{{ selectedLabel }}
          </a>
          <div class="placeholder">
            {{ placeholder }}
          </div>
        </div>
        <i
          :class="{'dropdown-arrow--open': shown}"
          class="icon-down dropdown-arrow"
        />
      </div>
      <template #popper="{ hide }">
        <div
          class="dropdown-body"
        >
          <a
            v-for="(option, index) in options"
            :key="index"
            :class="{'dropdown-item--capitalize': capitalize}"
            class="dropdown-item"
            @click="selectOption(index, option), hide()"
          >
            {{ option.value }}
          </a>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  props: {
    appearance: {
      type: String,
      default: 'primary',
      validator: val => ['primary', 'secondary'].includes(val),
    },
    options: { type: Array, default: () => [] },
    icon: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    ellipsisWidth: { type: String, default: '' },
    // Vue 3: v-model → `modelValue` (era `value`).
    modelValue: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    lineHeight: { type: Number, default: 30 },
    capitalize: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      shown: false,
    };
  },
  computed: {
    selected: {
      get() {
        return this.modelValue;
      },
      set(newValue) {
        this.$emit('update:modelValue', newValue);
      },
    },
    selectedLabel() {
      return (this.options.find(option => option.name === this.selected) || {}).value;
    },
    dropdownValueStyle() {
      const ellipsisStyle = this.ellipsisWidth ? {
        width: this.ellipsisWidth,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      } : {};
      return {
        ...ellipsisStyle,
        lineHeight: `${this.lineHeight}px`,
      };
    },
  },
  watch: {
    autofocus: {
      handler(value) {
        if (value) {
          setTimeout(() => {
            this.$refs.fakeInput.focus();
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    selectOption(index, option) {
      this.selected = option.name;
      this.$emit('change', option);
    },
    dropdownFocus() {
      this.shown = true;
    },
  },
};
</script>

<style lang="scss">


.dropdown-container {
  position: relative;

  .dropdown-hidden-input {
    opacity: 0;
    border: 0;
    height: 0;
    position: absolute;
    width: 0;
  }

  .dropdown-button-container {
    position: relative;
    align-items: center;
  }

  .dropdown-button-container .dropdown-icon .icon{
    color: $grey-gull;
  }

  &.dropdown-disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .dropdown-button-container .dropdown-button{
    position: relative;
    &--filled .dropdown-value {
      color: $black;
    }
  }

  .dropdown-button-container .dropdown-button .dropdown-value {
    padding: 0.5rem;
    display: block;
    line-height: 30px;
    margin-right: 1rem;
    &:after {
      content: '-';
      visibility: hidden;
    }
  }

  &.dropdown-secondary .dropdown-button-container .dropdown-icon {
    position: absolute;
    z-index: 2;
    left: 5px;
  }

 &.dropdown-secondary .dropdown-button-container .dropdown-icon ~ .dropdown-button .dropdown-value {
    padding-left: 30px;
  }

  .dropdown-button-container .dropdown-button .dropdown-value{
    padding: 0.25rem 0.5rem 0.25rem 0.8rem;
    color: $blue-east-bay;
    font-size: $size-9;
    &--capitalize {
      text-transform: capitalize;
    }
  }


  .dropdown-button-container .dropdown-button .placeholder {
    position: absolute;
    top: 13px;
    @include desktop {
      top: 10px;
    }
    left: 10px;
    color: $blue-steel;
    pointer-events: none;
    width: 100%;
  }

  &.dropdown-primary .dropdown-button-container .dropdown-button .placeholder {
    transition: all .3s cubic-bezier(0.4,0,0.2,1);
    transform-origin: bottom left;
  }

  &.dropdown-primary .dropdown-button-container .dropdown-button--filled .placeholder{
    transform: translate(-3px, -23px) scale(0.90);
  }
  &.dropdown-secondary .dropdown-button-container .dropdown-button .placeholder{
    font-size: $size-9;
    left: 8px;
    top: 8px;
  }
  &.dropdown-secondary .dropdown-button-container .dropdown-button--filled .placeholder{
    display: none;
  }


  &.dropdown-primary .dropdown-button-container .dropdown-button{
    box-shadow: 0 0 10px 0 rgba(69,86,107,0.10);
    border-radius: 5px;
    background-color: $white;
  }
  &.dropdown-secondary .dropdown-button-container .dropdown-button{
    border-radius: 5px;
    border: 1px solid $grey-border-dark;
    background-color: $white-old;
    font-size: $size-9;
    color: $blue-east-bay;
  }

  // Freccia: ex .bp-dropdown__btn .dropdown-arrow.
  .dropdown-button-container .dropdown-arrow {
    position: absolute;
    display: inline-flex;
    transition: all 0.4s ease-in-out;
    font-size: $size-5;
    color: $grey-gull;
    right: 5px;
    top: 9px;
    &--open {
      transform: rotate(0.5turn);
    }
  }
}

// Corpo del dropdown (slot #popper di floating-vue).
.dropdown-popper {
  .dropdown-body {
    max-height: 200px;
    overflow-y: auto;

    .dropdown-item {
      display: block;
      padding: 0.375rem 1rem;
      color: $blue-east-bay;
      &:hover{
        background-color: $white-seashell;
      }
      &--capitalize {
        text-transform: capitalize;
      }
    }
  }
}


</style>
